import { useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import useForm from '@hooks/useForm'
import { ClientService } from '@services/index'
import Capabilities from '@utils/capabilities'
import { GACategory } from '@utils/google-analytics'
import GoogleTagManager from '@utils/gtm/google-tag-manager'
import Storage from '@utils/storage'

type SignInFormData = {
  username?: string
  password?: string
  code?: string
}

const useSignInForm = (purchaseQuantity: number, titleId: string) => {
  const { t } = useTranslation()

  const { data, updateData, errors, setError, clearError } = useForm<SignInFormData>({})
  const codeRef = useRef<HTMLInputElement>(null)

  const [submitting, setSubmitting] = useState(false)
  const [requiresCode, setRequiresCode] = useState(false)
  const [signInError, setSignInError] = useState('')

  // Whether a user's password algorithm is outdated.
  const [outdatedPassword, setOutdatedPassword] = useState(false)
  const [forgotPwd, setForgotPwd] = useState(false)

  // region Sign In Functions
  const signInWithEmail = async (): Promise<void> => {
    clearError('username')
    const username = data.username?.trim()
    if (!username) {
      setError({ username: t('errors.usernameRequired') })
      return
    }

    setSubmitting(true)

    // Log the user in
    const [resp, err] = await ClientService.loginUser(username, data.password, data.code)

    // On successful login, reload the page
    if (resp) {
      GoogleTagManager.trackLegacyEvent(GACategory.WEBAPP, 'login_r3')

      // Used to open and set quantity when redirecting back
      Storage.setPurchasingQuantity(titleId, purchaseQuantity)

      window.location.reload()
    }
    // On unsuccessful login, show errors
    else if (err?.length) {
      const error = err[0]

      // If OTP is required, show the box and focus on it
      if (error.code === 'otpRequired') {
        setRequiresCode(true)
        codeRef.current?.focus()
      }
      // If outdated password, show "Forgot Password" screen
      else if (error.code === 'passwordAlgorithmUnsupported') {
        setOutdatedPassword(true)
        setForgotPwd(true)
      } else {
        if (error.code) setSignInError(t(`errors.${error.code}`))
      }
    }

    setSubmitting(false)
  }

  const referringHostname = (): string => {
    let hostname = window.location.hostname
    if (window.location.port.length > 0) hostname += `:${window.location.port}`
    return hostname
  }

  const signInWithGoogle = (): void => {
    if (Capabilities.cookiesEnabled()) {
      // Used to open and set quantity when redirecting back from Google
      Storage.setPurchasingQuantity(titleId, purchaseQuantity)

      const search = new URLSearchParams({
        return_to: window.location.toString(),
        referringHostname: referringHostname(),
      })
      window.location.href = `/auth/google?${search.toString()}`
    }
  }

  const signInWithFacebook = (): void => {
    if (Capabilities.cookiesEnabled()) {
      // Used to open and set quantity when redirecting back from Facebook
      Storage.setPurchasingQuantity(titleId, purchaseQuantity)

      const search = new URLSearchParams({
        return_to: window.location.toString(),
        referringHostname: referringHostname(),
      })
      window.location.href = `/auth/facebook?${search.toString()}`
    }
  }
  // endregion Sign In Functions

  return {
    actions: {
      signInWithEmail,
      signInWithFacebook,
      signInWithGoogle,
    },
    password: {
      forgot: forgotPwd,
      outdated: outdatedPassword,
      setForgot: setForgotPwd,
    },
    form: {
      data,
      errors,
      updateData,
      submitting,
      submitError: signInError,
      clearSubmitError: () => setSignInError(''),
    },
    code: {
      ref: codeRef,
      required: requiresCode,
    },
  }
}

export default useSignInForm
